import { FormField } from '@/components/form-section'
import {
    getTriggerEventLabels,
    type AutomationTriggerEvent,
} from '@/lib/api/sales/automations-api'
import type { SalesFlowTrigger } from '@/lib/api/sales/flows-api'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import {
    SALES_FLOW_DATE_FIELDS,
    SALES_FLOW_SUPPORTED_TRIGGERS,
    type SalesFlowTriggerType,
} from '@mediardex/contracts'
import { useState } from 'react'
import {
    MdAdd,
    MdDelete,
    MdExpandLess,
    MdExpandMore,
    MdFilterList,
} from 'react-icons/md'
import { FLOW_TRIGGER_LABELS, formatMinutes } from '../flow-meta'
import { findStage, type FlowRefData } from '../use-flow-ref-data'
import ConditionsEditor from './conditions-editor'
import DurationInput from './duration-input'

const TRIGGER_HINTS: Partial<Record<SalesFlowTriggerType, string>> = {
    EVENT: 'Algo acontece no CRM: lead criado, cobrança paga, agendamento...',
    STAGE_ENTERED:
        'A oportunidade é criada ou movida para a etapa. É o jeito de usar uma etapa "Follow-up".',
    TIME_IN_STAGE:
        'A oportunidade continua na etapa depois do tempo definido. Dispara uma vez a cada entrada na etapa.',
    DATE_FIELD_OFFSET:
        'Um tempo antes ou depois de uma data, como 24 horas antes do agendamento.',
    NO_REPLY:
        'A última mensagem da conversa foi nossa e o contato não respondeu no tempo definido.',
    INBOUND_AFTER_SILENCE:
        'O contato volta a escrever depois de ficar o tempo definido sem mandar mensagem.',
    OUTBOUND_READ:
        'O contato lê uma mensagem nossa no WhatsApp (API oficial ou WhatsApp Web). Com etapa, só quando a oportunidade aberta do lead está nela.',
    SCHEDULE_DAILY:
        'Todo dia, no horário, para cada oportunidade aberta na etapa escolhida.',
    CONVERSATION_CLOSED: 'O atendimento do lead é encerrado.',
    MANUAL: 'Só quando alguém inicia pela barra do chat ou pela tela do lead.',
}

const STAGE_REQUIRED: SalesFlowTriggerType[] = [
    'STAGE_ENTERED',
    'TIME_IN_STAGE',
    'SCHEDULE_DAILY',
]
const STAGE_OPTIONAL: SalesFlowTriggerType[] = ['EVENT', 'OUTBOUND_READ']

function triggerSummary(
    trigger: SalesFlowTrigger,
    refData: FlowRefData,
    eventLabels: Record<string, string>
): string {
    const parts: string[] = []
    if (trigger.type === 'EVENT' && trigger.event)
        parts.push(eventLabels[trigger.event] ?? trigger.event)
    else parts.push(FLOW_TRIGGER_LABELS[trigger.type])
    const found = findStage(refData, trigger.stageId)
    if (found) parts.push(`${found.pipeline.name} › ${found.stage.name}`)
    else if (trigger.pipelineId)
        parts.push(
            refData.pipelines.find(p => p.id === trigger.pipelineId)?.name ?? ''
        )
    const config = trigger.config ?? {}
    if (trigger.type === 'TIME_IN_STAGE' && config.afterMinutes)
        parts.push(`há ${formatMinutes(config.afterMinutes)}`)
    if (trigger.type === 'NO_REPLY' && config.afterMinutes)
        parts.push(`há ${formatMinutes(config.afterMinutes)}`)
    if (trigger.type === 'INBOUND_AFTER_SILENCE' && config.silenceMinutes)
        parts.push(`após ${formatMinutes(config.silenceMinutes)}`)
    if (trigger.type === 'SCHEDULE_DAILY' && config.time)
        parts.push(`às ${config.time}`)
    if (trigger.type === 'DATE_FIELD_OFFSET' && config.field) {
        const offset = Number(config.offsetMinutes) || 0
        const label =
            SALES_FLOW_DATE_FIELDS.find(
                f => f.value === config.field
            )?.label.toLowerCase() ?? config.field
        parts.push(
            offset === 0
                ? `na ${label}`
                : `${formatMinutes(Math.abs(offset))} ${offset < 0 ? 'antes da' : 'depois da'} ${label}`
        )
    }
    if (trigger.delayMinutes)
        parts.push(`depois de ${formatMinutes(trigger.delayMinutes)}`)
    if (trigger.conditions?.length)
        parts.push(`${trigger.conditions.length} filtro(s)`)
    return parts.filter(Boolean).join(' · ')
}

/** Gatilhos do fluxo: qualquer um deles inicia (OU). */
export default function TriggersPanel({
    triggers,
    refData,
    onChange,
}: {
    triggers: SalesFlowTrigger[]
    refData: FlowRefData
    onChange: (triggers: SalesFlowTrigger[]) => void
}) {
    const eventLabels = getTriggerEventLabels(useServiceLabel()) as Record<
        string,
        string
    >
    const [openIndex, setOpenIndex] = useState<number | null>(
        triggers.length ? null : 0
    )

    const update = (index: number, patch: Partial<SalesFlowTrigger>) =>
        onChange(triggers.map((t, i) => (i === index ? { ...t, ...patch } : t)))

    const add = () => {
        onChange([...triggers, { type: 'STAGE_ENTERED', delayMinutes: 0 }])
        setOpenIndex(triggers.length)
    }

    return (
        <div className="space-y-3">
            <p className="text-xs leading-relaxed text-base-content/50">
                Qualquer gatilho da lista inicia o fluxo para o lead.
            </p>

            {triggers.map((trigger, index) => {
                const open = openIndex === index
                const pipeline = refData.pipelines.find(
                    p => p.id === Number(trigger.pipelineId)
                )
                const idp = `trigger-${index}`
                return (
                    <div
                        key={index}
                        className={`rounded-xl border bg-base-100 ${open ? 'border-primary/40 shadow-sm' : 'border-base-300'}`}
                    >
                        <button
                            type="button"
                            className="flex w-full items-start gap-2 p-3 text-left"
                            onClick={() => setOpenIndex(open ? null : index)}
                        >
                            <span className="badge badge-success badge-sm mt-0.5">
                                {index + 1}
                            </span>
                            <span className="min-w-0 flex-1 text-sm leading-snug">
                                {triggerSummary(trigger, refData, eventLabels)}
                            </span>
                            {open ? (
                                <MdExpandLess className="mt-0.5 shrink-0" />
                            ) : (
                                <MdExpandMore className="mt-0.5 shrink-0" />
                            )}
                        </button>

                        {open && (
                            <div className="space-y-4 border-t border-base-200 p-3">
                                <FormField
                                    label="Quando"
                                    htmlFor={`${idp}-type`}
                                    hint={TRIGGER_HINTS[trigger.type]}
                                >
                                    <select
                                        id={`${idp}-type`}
                                        className="select select-bordered select-sm w-full"
                                        value={trigger.type}
                                        onChange={e =>
                                            update(index, {
                                                type: e.target
                                                    .value as SalesFlowTriggerType,
                                                event: null,
                                                stageId: null,
                                                config: null,
                                            })
                                        }
                                    >
                                        {SALES_FLOW_SUPPORTED_TRIGGERS.map(
                                            type => (
                                                <option key={type} value={type}>
                                                    {FLOW_TRIGGER_LABELS[type]}
                                                </option>
                                            )
                                        )}
                                    </select>
                                </FormField>

                                {trigger.type === 'EVENT' && (
                                    <FormField
                                        label="Evento"
                                        htmlFor={`${idp}-event`}
                                        required
                                    >
                                        <select
                                            id={`${idp}-event`}
                                            className="select select-bordered select-sm w-full"
                                            value={trigger.event ?? ''}
                                            onChange={e =>
                                                update(index, {
                                                    event: (e.target.value ||
                                                        null) as AutomationTriggerEvent | null,
                                                })
                                            }
                                        >
                                            <option value="">
                                                Selecione o evento
                                            </option>
                                            {Object.entries(eventLabels).map(
                                                ([value, label]) => (
                                                    <option
                                                        key={value}
                                                        value={value}
                                                    >
                                                        {label}
                                                    </option>
                                                )
                                            )}
                                        </select>
                                    </FormField>
                                )}

                                {(STAGE_OPTIONAL.includes(trigger.type) ||
                                    STAGE_REQUIRED.includes(trigger.type)) && (
                                    <>
                                        <FormField
                                            label="Funil"
                                            htmlFor={`${idp}-pipeline`}
                                            optional={STAGE_OPTIONAL.includes(
                                                trigger.type
                                            )}
                                            required={
                                                !STAGE_OPTIONAL.includes(
                                                    trigger.type
                                                )
                                            }
                                        >
                                            <select
                                                id={`${idp}-pipeline`}
                                                className="select select-bordered select-sm w-full"
                                                value={trigger.pipelineId ?? ''}
                                                onChange={e =>
                                                    update(index, {
                                                        pipelineId: e.target
                                                            .value
                                                            ? Number(
                                                                  e.target.value
                                                              )
                                                            : null,
                                                        stageId: null,
                                                    })
                                                }
                                            >
                                                <option value="">
                                                    {STAGE_OPTIONAL.includes(
                                                        trigger.type
                                                    )
                                                        ? 'Qualquer funil'
                                                        : 'Selecione o funil'}
                                                </option>
                                                {refData.pipelines.map(p => (
                                                    <option
                                                        key={p.id}
                                                        value={p.id}
                                                    >
                                                        {p.name}
                                                        {p.company?.name
                                                            ? ` (${p.company.name})`
                                                            : ''}
                                                    </option>
                                                ))}
                                            </select>
                                        </FormField>
                                        <FormField
                                            label="Etapa"
                                            htmlFor={`${idp}-stage`}
                                            optional={STAGE_OPTIONAL.includes(
                                                trigger.type
                                            )}
                                            required={
                                                !STAGE_OPTIONAL.includes(
                                                    trigger.type
                                                )
                                            }
                                        >
                                            <select
                                                id={`${idp}-stage`}
                                                className="select select-bordered select-sm w-full"
                                                value={trigger.stageId ?? ''}
                                                disabled={!pipeline}
                                                onChange={e =>
                                                    update(index, {
                                                        stageId: e.target.value
                                                            ? Number(
                                                                  e.target.value
                                                              )
                                                            : null,
                                                    })
                                                }
                                            >
                                                <option value="">
                                                    {pipeline
                                                        ? STAGE_OPTIONAL.includes(
                                                              trigger.type
                                                          )
                                                            ? 'Qualquer etapa'
                                                            : 'Selecione a etapa'
                                                        : 'Escolha o funil primeiro'}
                                                </option>
                                                {(pipeline?.stages ?? []).map(
                                                    stage => (
                                                        <option
                                                            key={stage.id}
                                                            value={stage.id}
                                                        >
                                                            {stage.name}
                                                        </option>
                                                    )
                                                )}
                                            </select>
                                        </FormField>
                                    </>
                                )}

                                {(trigger.type === 'TIME_IN_STAGE' ||
                                    trigger.type === 'NO_REPLY') && (
                                    <FormField
                                        label={
                                            trigger.type === 'TIME_IN_STAGE'
                                                ? 'Parada na etapa há'
                                                : 'Sem resposta há'
                                        }
                                        htmlFor={`${idp}-after`}
                                        required
                                    >
                                        <DurationInput
                                            id={`${idp}-after`}
                                            value={trigger.config?.afterMinutes}
                                            min={1}
                                            onChange={afterMinutes =>
                                                update(index, {
                                                    config: {
                                                        ...(trigger.config ??
                                                            {}),
                                                        afterMinutes,
                                                    },
                                                })
                                            }
                                        />
                                    </FormField>
                                )}

                                {trigger.type === 'INBOUND_AFTER_SILENCE' && (
                                    <FormField
                                        label="Silêncio mínimo"
                                        htmlFor={`${idp}-silence`}
                                        required
                                        hint="Tempo desde a mensagem anterior do contato."
                                    >
                                        <DurationInput
                                            id={`${idp}-silence`}
                                            value={
                                                trigger.config?.silenceMinutes
                                            }
                                            min={1}
                                            onChange={silenceMinutes =>
                                                update(index, {
                                                    config: {
                                                        ...(trigger.config ??
                                                            {}),
                                                        silenceMinutes,
                                                    },
                                                })
                                            }
                                        />
                                    </FormField>
                                )}

                                {trigger.type === 'DATE_FIELD_OFFSET' && (
                                    <>
                                        <FormField
                                            label="Data"
                                            htmlFor={`${idp}-field`}
                                            required
                                        >
                                            <select
                                                id={`${idp}-field`}
                                                className="select select-bordered select-sm w-full"
                                                value={
                                                    trigger.config?.field ?? ''
                                                }
                                                onChange={e =>
                                                    update(index, {
                                                        config: {
                                                            ...(trigger.config ??
                                                                {}),
                                                            field:
                                                                e.target
                                                                    .value ||
                                                                undefined,
                                                        },
                                                    })
                                                }
                                            >
                                                <option value="">
                                                    Selecione a data
                                                </option>
                                                {SALES_FLOW_DATE_FIELDS.map(
                                                    field => (
                                                        <option
                                                            key={field.value}
                                                            value={field.value}
                                                        >
                                                            {field.label}
                                                        </option>
                                                    )
                                                )}
                                            </select>
                                        </FormField>
                                        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2">
                                            <FormField
                                                label="Quanto tempo"
                                                htmlFor={`${idp}-offset`}
                                                required
                                            >
                                                <DurationInput
                                                    id={`${idp}-offset`}
                                                    value={Math.abs(
                                                        Number(
                                                            trigger.config
                                                                ?.offsetMinutes
                                                        ) || 0
                                                    )}
                                                    onChange={minutes => {
                                                        const before =
                                                            Number(
                                                                trigger.config
                                                                    ?.offsetMinutes
                                                            ) < 0 ||
                                                            trigger.config
                                                                ?.offsetMinutes ===
                                                                undefined
                                                        update(index, {
                                                            config: {
                                                                ...(trigger.config ??
                                                                    {}),
                                                                offsetMinutes:
                                                                    before
                                                                        ? -minutes
                                                                        : minutes,
                                                            },
                                                        })
                                                    }}
                                                />
                                            </FormField>
                                            <select
                                                aria-label="Antes ou depois"
                                                className="select select-bordered select-sm"
                                                value={
                                                    Number(
                                                        trigger.config
                                                            ?.offsetMinutes
                                                    ) > 0
                                                        ? 'after'
                                                        : 'before'
                                                }
                                                onChange={e => {
                                                    const minutes = Math.abs(
                                                        Number(
                                                            trigger.config
                                                                ?.offsetMinutes
                                                        ) || 0
                                                    )
                                                    update(index, {
                                                        config: {
                                                            ...(trigger.config ??
                                                                {}),
                                                            offsetMinutes:
                                                                e.target
                                                                    .value ===
                                                                'after'
                                                                    ? minutes
                                                                    : -minutes,
                                                        },
                                                    })
                                                }}
                                            >
                                                <option value="before">
                                                    antes
                                                </option>
                                                <option value="after">
                                                    depois
                                                </option>
                                            </select>
                                        </div>
                                    </>
                                )}

                                {trigger.type === 'SCHEDULE_DAILY' && (
                                    <FormField
                                        label="Horário"
                                        htmlFor={`${idp}-time`}
                                        required
                                        hint="Horário de Brasília."
                                    >
                                        <input
                                            id={`${idp}-time`}
                                            type="time"
                                            className="input input-bordered input-sm w-full"
                                            value={trigger.config?.time ?? ''}
                                            onChange={e =>
                                                update(index, {
                                                    config: {
                                                        ...(trigger.config ??
                                                            {}),
                                                        time: e.target.value,
                                                        timezone:
                                                            'America/Sao_Paulo',
                                                    },
                                                })
                                            }
                                        />
                                    </FormField>
                                )}

                                {trigger.type !== 'MANUAL' && (
                                    <FormField
                                        label="Esperar antes de começar"
                                        htmlFor={`${idp}-delay`}
                                        optional
                                        hint="Zero começa na hora. Na etapa, o fluxo só começa se a oportunidade ainda estiver nela."
                                    >
                                        <DurationInput
                                            id={`${idp}-delay`}
                                            value={trigger.delayMinutes ?? 0}
                                            onChange={delayMinutes =>
                                                update(index, { delayMinutes })
                                            }
                                        />
                                    </FormField>
                                )}

                                <FormField
                                    label="Filtros"
                                    icon={<MdFilterList />}
                                    optional
                                >
                                    <ConditionsEditor
                                        conditions={trigger.conditions ?? []}
                                        logic={trigger.conditionLogic ?? 'AND'}
                                        emptyText="Sem filtros: vale para todo lead."
                                        onChange={(
                                            conditions,
                                            conditionLogic
                                        ) =>
                                            update(index, {
                                                conditions,
                                                conditionLogic,
                                            })
                                        }
                                    />
                                </FormField>

                                <div className="flex justify-end">
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm gap-1 text-error"
                                        onClick={() => {
                                            onChange(
                                                triggers.filter(
                                                    (_, i) => i !== index
                                                )
                                            )
                                            setOpenIndex(null)
                                        }}
                                    >
                                        <MdDelete /> Remover gatilho
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )
            })}

            <button
                type="button"
                className="btn btn-outline btn-sm w-full gap-1"
                onClick={add}
            >
                <MdAdd /> Adicionar gatilho
            </button>
        </div>
    )
}
